import React, { useState, useEffect } from 'react';
import { Save, DollarSign, User, Baby, Users, Calculator, MapPin, RefreshCw, Edit, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { api } from '../../utils/apiClient';

const PricingManagement = () => {
  const [pricing, setPricing] = useState({
    adult_percentage: 100,
    child_percentage: 75,
    baby_percentage: 10
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [basePrice, setBasePrice] = useState(3666); // Precio de ejemplo
  
  // Route pricing state
  const [routes, setRoutes] = useState([
    { origin: 'Oaxaca de Juárez', destination: 'Puerto Escondido', price: 4500, key: 'OAX-PXM' },
    { origin: 'Puerto Escondido', destination: 'Oaxaca de Juárez', price: 3666, key: 'PXM-OAX' },
    { origin: 'Oaxaca de Juárez', destination: 'Huatulco', price: 3666, key: 'OAX-HUX' },
    { origin: 'Huatulco', destination: 'Oaxaca de Juárez', price: 3800, key: 'HUX-OAX' }
  ]);
  const [updatingRoute, setUpdatingRoute] = useState(false);
  const [editingRoute, setEditingRoute] = useState(null);
  const [routeEditForm, setRouteEditForm] = useState({
    oldOrigin: '',
    oldDestination: '',
    newOrigin: '',
    newDestination: '',
    newDepartureTime: '',
    newArrivalTime: '',
    newPrice: '',
    newDaysOfWeek: 'Lunes a Sábado'
  });

  useEffect(() => {
    loadPricing();
    loadRoutes();
  }, []);

  const loadPricing = async () => {
    try {
      const response = await api.get('/api/admin/pricing/config');
      setPricing(response);
    } catch (error) {
      console.error('Error loading pricing:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRoutes = async () => {
    try {
      // Get unique routes from flights
      const response = await api.get('/api/admin/flights/all');
      const flights = response.flights || [];
      
      // Group by origin-destination to get unique routes
      const routeMap = new Map();
      flights.forEach(flight => {
        const key = `${flight.origin}-${flight.destination}`;
        if (!routeMap.has(key)) {
          routeMap.set(key, {
            origin: flight.origin,
            destination: flight.destination,
            price: flight.price,
            key: key
          });
        }
      });
      
      setRoutes(Array.from(routeMap.values()));
    } catch (error) {
      console.error('Error loading routes:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/api/admin/pricing/config', pricing);
      alert('Tarifas actualizadas exitosamente');
    } catch (error) {
      console.error('Error saving pricing:', error);
      alert('Error al actualizar tarifas');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateRoutePrice = async (route) => {
    setUpdatingRoute(true);
    try {
      const response = await api.post('/api/admin/flights/bulk-update-by-route', {
        origin: route.origin,
        destination: route.destination,
        newPrice: route.price
      });
      
      alert(`✅ ${response.updated_count} vuelos actualizados en la ruta ${route.origin} → ${route.destination}`);
      await loadRoutes(); // Reload to get updated prices
    } catch (error) {
      console.error('Error updating route price:', error);
      alert('❌ Error al actualizar precio de ruta');
    } finally {
      setUpdatingRoute(false);
    }
  };

  const updateRoutePrice = (key, newPrice) => {
    setRoutes(routes.map(r => 
      r.key === key ? { ...r, price: parseFloat(newPrice) } : r
    ));
  };

  const handleEditRoute = (route) => {
    setEditingRoute(route.key);
    setRouteEditForm({
      oldOrigin: route.origin,
      oldDestination: route.destination,
      newOrigin: route.origin,
      newDestination: route.destination,
      newDepartureTime: '',
      newArrivalTime: '',
      newPrice: route.price.toString(),
      newDaysOfWeek: 'Lunes a Sábado'
    });
  };

  const handleSaveRouteEdit = async () => {
    setUpdatingRoute(true);
    try {
      const updates = {
        origin: routeEditForm.newOrigin,
        destination: routeEditForm.newDestination,
        price: parseFloat(routeEditForm.newPrice)
      };
      
      if (routeEditForm.newDepartureTime) {
        updates.departureTime = routeEditForm.newDepartureTime;
      }
      if (routeEditForm.newArrivalTime) {
        updates.arrivalTime = routeEditForm.newArrivalTime;
      }
      if (routeEditForm.newDaysOfWeek) {
        updates.daysOfWeek = routeEditForm.newDaysOfWeek;
      }
      
      const response = await api.post('/api/admin/flights/bulk-update-by-route', {
        origin: routeEditForm.oldOrigin,
        destination: routeEditForm.oldDestination,
        updates: updates
      });
      
      alert(`✅ ${response.updated_count} vuelos actualizados correctamente`);
      setEditingRoute(null);
      await loadRoutes();
    } catch (error) {
      console.error('Error updating route:', error);
      alert('❌ Error al actualizar ruta completa');
    } finally {
      setUpdatingRoute(false);
    }
  };

  const calculatePrice = (percentage) => {
    // Usa un precio de ejemplo para la calculadora (no afecta los cálculos reales)
    // En el booking, se usa el precio del vuelo específico
    const examplePrice = 5500; // Precio de ejemplo para mostrar
    return Math.round(examplePrice * (percentage / 100));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Gestión de Tarifas</h2>
          <p className="text-gray-600 mb-2">Configura los porcentajes que se aplicarán sobre el <strong>precio del vuelo</strong></p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
            <p className="text-sm text-blue-900">
              💡 Estos porcentajes se calculan sobre el precio de cada vuelo al momento de la reserva
            </p>
          </div>
        </div>
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-green-600 hover:bg-green-700"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Guardando...' : 'Guardar Cambios'}
        </Button>
      </div>

      {/* Pricing Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Adultos */}
        <Card className="border-2 border-blue-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                  <User className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-bold text-lg">Adultos</h3>
                  <p className="text-sm text-gray-500">12+ años</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  Porcentaje del precio base
                </label>
                <div className="flex items-center space-x-3">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={pricing.adult_percentage}
                    onChange={(e) => setPricing({...pricing, adult_percentage: parseFloat(e.target.value)})}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <span className="text-2xl font-bold text-blue-600">%</span>
                </div>
              </div>
              
              <div className="pt-4 border-t">
                <p className="text-sm text-gray-600">Ejemplo con vuelo de $5,500:</p>
                <p className="text-3xl font-bold text-blue-900">
                  ${calculatePrice(pricing.adult_percentage).toLocaleString()}
                </p>
                <p className="text-sm text-gray-500">MXN por persona</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Niños */}
        <Card className="border-2 border-green-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                  <Users className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-bold text-lg">Niños</h3>
                  <p className="text-sm text-gray-500">2-11 años</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  Porcentaje del precio base
                </label>
                <div className="flex items-center space-x-3">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={pricing.child_percentage}
                    onChange={(e) => setPricing({...pricing, child_percentage: parseFloat(e.target.value)})}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                  <span className="text-2xl font-bold text-green-600">%</span>
                </div>
              </div>
              
              <div className="pt-4 border-t">
                <p className="text-sm text-gray-600">Ejemplo con vuelo de $5,500:</p>
                <p className="text-3xl font-bold text-green-900">
                  ${calculatePrice(pricing.child_percentage).toLocaleString()}
                </p>
                <p className="text-sm text-gray-500">MXN por persona</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bebés */}
        <Card className="border-2 border-amber-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mr-3">
                  <Baby className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-bold text-lg">Bebés</h3>
                  <p className="text-sm text-gray-500">0-2 años</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  Porcentaje del precio base
                </label>
                <div className="flex items-center space-x-3">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={pricing.baby_percentage}
                    onChange={(e) => setPricing({...pricing, baby_percentage: parseFloat(e.target.value)})}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  />
                  <span className="text-2xl font-bold text-amber-600">%</span>
                </div>
              </div>
              
              <div className="pt-4 border-t">
                <p className="text-sm text-gray-600">Ejemplo con vuelo de $5,500:</p>
                <p className="text-3xl font-bold text-amber-900">
                  ${calculatePrice(pricing.baby_percentage).toLocaleString()}
                </p>
                <p className="text-sm text-gray-500">MXN por persona</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Route Pricing Section */}
      <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <MapPin className="w-6 h-6 text-purple-600 mr-2" />
              <div>
                <h3 className="text-xl font-bold text-gray-900">Precios por Ruta</h3>
                <p className="text-sm text-gray-600">Actualiza el precio base de cada ruta específica o edita la ruta completa</p>
              </div>
            </div>
            <Button
              onClick={loadRoutes}
              className="bg-purple-600 hover:bg-purple-700"
              size="sm"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Recargar Rutas
            </Button>
          </div>

          {/* Edit Route Modal */}
          {editingRoute && (
            <div className="mb-6 bg-orange-50 border-2 border-orange-300 rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-bold text-orange-900">Editar Ruta Completa</h4>
                <Button onClick={() => setEditingRoute(null)} variant="outline" size="sm">
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-1 block">Nuevo Origen *</label>
                  <input
                    type="text"
                    value={routeEditForm.newOrigin}
                    onChange={(e) => setRouteEditForm({...routeEditForm, newOrigin: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="Ej: Ciudad de México, Cancún"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-1 block">Nuevo Destino *</label>
                  <input
                    type="text"
                    value={routeEditForm.newDestination}
                    onChange={(e) => setRouteEditForm({...routeEditForm, newDestination: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="Ej: Guadalajara, Monterrey"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-1 block">Nuevo Precio (MXN) *</label>
                  <input
                    type="number"
                    value={routeEditForm.newPrice}
                    onChange={(e) => setRouteEditForm({...routeEditForm, newPrice: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-1 block">Nuevos Días de Operación</label>
                  <select
                    value={routeEditForm.newDaysOfWeek}
                    onChange={(e) => setRouteEditForm({...routeEditForm, newDaysOfWeek: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="Lunes a Sábado">Lunes a Sábado</option>
                    <option value="Domingo">Solo Domingo</option>
                    <option value="Lunes a Viernes">Lunes a Viernes</option>
                    <option value="Sábados y Domingos">Sábados y Domingos</option>
                    <option value="Todos los días">Todos los días</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-1 block">Nueva Hora Salida (opcional)</label>
                  <input
                    type="time"
                    value={routeEditForm.newDepartureTime}
                    onChange={(e) => setRouteEditForm({...routeEditForm, newDepartureTime: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-1 block">Nueva Hora Llegada (opcional)</label>
                  <input
                    type="time"
                    value={routeEditForm.newArrivalTime}
                    onChange={(e) => setRouteEditForm({...routeEditForm, newArrivalTime: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>
              <div className="mt-4 flex space-x-3">
                <Button
                  onClick={handleSaveRouteEdit}
                  disabled={updatingRoute}
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {updatingRoute ? 'Actualizando...' : 'Guardar Cambios en Todos los Vuelos Futuros'}
                </Button>
                <Button onClick={() => setEditingRoute(null)} variant="outline">
                  Cancelar
                </Button>
              </div>
              <p className="text-xs text-orange-700 mt-2">
                ⚠️ Esto actualizará TODOS los vuelos futuros de la ruta {routeEditForm.oldOrigin} → {routeEditForm.oldDestination}
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {routes.map((route) => (
              <div key={route.key} className="bg-white rounded-lg p-4 border-2 border-gray-200 hover:border-purple-300 transition-colors">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center text-sm text-gray-600 mb-1">
                      <span className="font-semibold text-gray-900">{route.origin}</span>
                      <span className="mx-2">→</span>
                      <span className="font-semibold text-gray-900">{route.destination}</span>
                    </div>
                    <p className="text-xs text-gray-500">Código: {route.key}</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <div className="flex-1">
                    <label className="text-xs text-gray-600 mb-1 block">Precio Base (MXN)</label>
                    <input
                      type="number"
                      value={route.price}
                      onChange={(e) => updateRoutePrice(route.key, e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <Button
                    onClick={() => handleUpdateRoutePrice(route)}
                    disabled={updatingRoute}
                    className="bg-green-600 hover:bg-green-700 mt-5"
                    size="sm"
                    title="Guardar solo precio"
                  >
                    <Save className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={() => handleEditRoute(route)}
                    className="bg-blue-600 hover:bg-blue-700 mt-5"
                    size="sm"
                    title="Editar ruta completa"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  💾 Guardar precio | ✏️ Editar ruta completa (origen, destino, horarios, días)
                </p>
              </div>
            ))}
          </div>

          <div className="mt-4 bg-purple-100 border border-purple-300 rounded-lg p-3">
            <p className="text-sm text-purple-900">
              💡 <strong>Importante:</strong> Al actualizar el precio de una ruta, se cambiará el precio de TODOS los vuelos futuros en esa ruta.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Calculadora de Ejemplo */}
      <Card className="bg-gradient-to-br from-blue-50 to-indigo-50">
        <CardContent className="p-6">
          <div className="flex items-center mb-4">
            <Calculator className="w-6 h-6 text-blue-600 mr-2" />
            <h3 className="text-xl font-bold text-gray-900">Calculadora de Ejemplo</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">
                Precio base del vuelo
              </label>
              <div className="flex items-center space-x-2">
                <DollarSign className="w-5 h-5 text-gray-400" />
                <input
                  type="number"
                  value={basePrice}
                  onChange={(e) => setBasePrice(parseFloat(e.target.value))}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-gray-600">MXN</span>
              </div>
            </div>
            
            <div className="space-y-2">
              <h4 className="font-semibold text-gray-700 mb-3">Ejemplo de reserva familiar:</h4>
              <div className="bg-white rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">2 Adultos × ${calculatePrice(pricing.adult_percentage).toLocaleString()}</span>
                  <span className="font-semibold">${(calculatePrice(pricing.adult_percentage) * 2).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">1 Niño × ${calculatePrice(pricing.child_percentage).toLocaleString()}</span>
                  <span className="font-semibold">${calculatePrice(pricing.child_percentage).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">1 Bebé × ${calculatePrice(pricing.baby_percentage).toLocaleString()}</span>
                  <span className="font-semibold">${calculatePrice(pricing.baby_percentage).toLocaleString()}</span>
                </div>
                <div className="border-t pt-2 mt-2 flex justify-between">
                  <span className="font-bold text-gray-900">Total:</span>
                  <span className="text-2xl font-bold text-green-600">
                    ${(calculatePrice(pricing.adult_percentage) * 2 + 
                       calculatePrice(pricing.child_percentage) + 
                       calculatePrice(pricing.baby_percentage)).toLocaleString()} MXN
                  </span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-blue-900 mb-2">ℹ️ Información importante:</h4>
        <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
          <li>Los cambios se aplican inmediatamente a todas las nuevas reservas</li>
          <li>Las reservas existentes mantienen el precio con el que fueron creadas</li>
          <li>El porcentaje se aplica sobre el precio base del vuelo</li>
          <li>Puedes configurar porcentajes personalizados para cada categoría</li>
        </ul>
      </div>
    </div>
  );
};

export default PricingManagement;
